import { useCallback, useEffect, useRef } from "react";

import { debounce, type DebouncedFunction } from "./debounce";

export function useStableDebouncedCallback<TArgs extends unknown[]>(
  fn: (...args: TArgs) => void,
  waitMs: number,
): DebouncedFunction<TArgs> {
  const latestFnRef = useRef<typeof fn>(fn);
  useEffect(() => {
    latestFnRef.current = fn;
  }, [fn]);

  const debouncedRef = useRef<DebouncedFunction<TArgs> | null>(null);

  useEffect(() => {
    const d = debounce<TArgs>(
      ((...args: TArgs) => {
        latestFnRef.current(...args);
      }) as (...args: TArgs) => unknown,
      waitMs,
    );
    debouncedRef.current = d;
    return () => d.cancel();
  }, [waitMs]);

  const callable = useCallback(
    ((...args: TArgs) => {
      debouncedRef.current?.(...args);
    }) as DebouncedFunction<TArgs>,
    [],
  );

  callable.cancel = () => debouncedRef.current?.cancel();
  callable.flush = () => debouncedRef.current?.flush();

  return callable;
}
